import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface PrefilledDocumentBadgeProps {
  contractTitle: string;
  /** Where the "Change" link points — typically `/contract-checker/new`. */
  changeHref: string;
}

/**
 * Shown in place of `<FileDropZone>` when the user arrives at `/new`
 * with a `?contractId` already selected. Communicates that we're
 * analyzing an existing contract from their library and gives them an
 * escape hatch to swap to a fresh upload.
 */
export function PrefilledDocumentBadge({
  contractTitle,
  changeHref,
}: PrefilledDocumentBadgeProps) {
  const { t } = useTranslation();

  return (
    <div className="md:col-span-7 flex flex-col">
      <span className="block mb-2 text-[11px] font-semibold uppercase tracking-wider text-globe-grey shrink-0">
        {t('ORGCONTRACTCHECKER_LABEL_INGESTION', { defaultValue: 'Document ingestion' })}
      </span>

      <div className="border-2 border-dashed border-selise-blue/40 bg-selise-blue/5 rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-56 sm:min-h-64 flex-1 text-center">
        <div className="w-14 h-14 rounded-full bg-card border border-border flex items-center justify-center mb-4">
          <FileText className="w-7 h-7 text-selise-blue" />
        </div>

        <span className="text-[10px] font-semibold uppercase tracking-widest text-selise-blue mb-2">
          {t('ORGCONTRACTCHECKER_NEW_FROM_CONTRACT', { defaultValue: 'From your Contracts' })}
        </span>

        <p className="text-sm font-bold text-[hsl(var(--fg))] max-w-full break-words">
          {contractTitle}
        </p>

        <Link
          to={changeHref}
          className="mt-3 text-xs font-semibold text-selise-blue hover:underline"
        >
          {t('ORGCONTRACTCHECKER_NEW_CHANGE', { defaultValue: 'Change' })}
        </Link>
      </div>
    </div>
  );
}

export default PrefilledDocumentBadge;
